import React, {
  FormEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  FormattedMessage,
  injectIntl,
  WrappedComponentProps,
} from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { Form, Label, Message, Table } from 'semantic-ui-react';
import additionalMessages from '../../messages';
import { postWithAuth } from '../../api';
import { fetchSites } from '../mainframe/mainframe.reducer';
import { toast } from 'react-toastify';
import { Dispatch } from 'redux';
import { decodeLatLong } from '../../utils/useful_functions';
import {
  buildSiteCsvPostData,
  getDefaultSiteCsvColumnTypes,
  parseSiteCSV,
} from './siteCsvImportUtils';
import {
  getImportErrorMessageId,
  getSampleSitesCsvUrl,
} from './siteImportConfig';
import type {
  SiteCsvColumnErrorType,
  SiteCsvColumnType,
} from './siteCsvImportUtils';
import type {
  SiteGroupKind,
  SiteImportSubmitState,
} from './siteImportModalTypes';

export type CSVSiteValidationPropsType = {
  data: string[][];
  skipFirstRow: boolean;
  groupKind: SiteGroupKind;
  closeFunc: () => void;
  onSubmitStateChange: (state: SiteImportSubmitState | null) => void;
} & WrappedComponentProps;

export const PAGE_SIZE = 20;

export const createSites = (
  dispatch: Dispatch<any>,
  projectId: string,
  validData: any[],
  groupKind: SiteGroupKind,
  closeFunc: () => void,
  heightUnits: string,
  postData?: any
) => {
  const is_network_site = groupKind === 'network_sites';
  const siteData = postData
    ? postData
    : validData.map(({ data }) => {
        const [
          name,
          latitude,
          longitude,
          maximum_height,
          description,
          node_type,
        ] = data;
        return {
          name,
          latitude: decodeLatLong(latitude, true)[0],
          longitude: decodeLatLong(longitude, false)[0],
          maximum_height: parseFloat(maximum_height),
          height_units: heightUnits,
          description,
          is_network_site,
          node_type: node_type,
        };
      });

  return postWithAuth(`project/${projectId}/sites`, siteData)
    .then(() => {
      dispatch(fetchSites(projectId));
      closeFunc();
    })
    .catch((err) => {
      console.error(err);
      if (err.detail) {
        toast(<Message error>{err.detail}</Message>, {
          autoClose: false,
        });
      }
      closeFunc();
    });
};

function CSVSiteValidation(props: CSVSiteValidationPropsType) {
  const {
    data,
    closeFunc,
    skipFirstRow = true,
    groupKind = 'network_sites',
    onSubmitStateChange,
    intl,
  } = props;
  const { formatMessage } = intl;
  const dispatch = useDispatch();
  const isNetworkSites = groupKind === 'network_sites';
  const { projectId, networkSiteCount, subscriberSiteCount } = useSelector(
    (state: any) => state.mainFrame
  );
  const sites = useSelector((state: any) =>
    isNetworkSites ? state.mainFrame.networkSites : state.mainFrame.subscriberSites
  );
  const [networkSiteNameTemplate, subscriberSiteNameTemplate] = useSelector(
    (state: any) => [
      state.map.networkSiteNameTemplate,
      state.map.subscriberSiteNameTemplate,
    ]
  );
  const siteNameTemplate = isNetworkSites
    ? networkSiteNameTemplate
    : subscriberSiteNameTemplate;
  const siteCount = isNetworkSites ? networkSiteCount : subscriberSiteCount;
  const [slicedData, setSlicedData] = useState<any[]>([]);
  const [gridData, setGridData] = useState<any[]>([]);
  const [columnTypes, setColumnTypes] = useState<SiteCsvColumnType[]>([]);
  const [errData, setErrData] = useState<any>({
    error: false,
    warning: false,
    truncate: false,
    siteWithNearSites: [],
    errorSites: [],
    importableRows: [],
    isEmpty: false,
    columnError: null,
  });
  const [formValues, setFormValues] = useState({
    skipFirstRow,
  });
  const [isLoading, setIsLoading] = useState(false);
  const totalSites = useRef(0);

  const columnCount = useMemo(() => {
    return data.reduce((maxCols, row) => Math.max(maxCols, row.length), 0);
  }, [data]);

  useEffect(() => {
    setColumnTypes(getDefaultSiteCsvColumnTypes(columnCount, isNetworkSites));
  }, [columnCount, isNetworkSites]);

  useEffect(() => {
    if (columnTypes.length === 0 && columnCount > 0) {
      return;
    }

    const parsed = parseSiteCSV(data, sites?.features ?? [], {
      skipFirstRow: formValues.skipFirstRow,
      areNetworkSites: isNetworkSites,
      columnTypes,
      siteNameTemplate,
      count: siteCount,
    });

    totalSites.current = parsed.totalRows;
    setErrData({
      warning: parsed.siteWithNearSites.length > 0,
      truncate: parsed.truncData.length > 0,
      error:
        Boolean(parsed.columnError) ||
        parsed.errorData.length > 0 ||
        parsed.isEmpty,
      siteWithNearSites: parsed.siteWithNearSites,
      errorSites: parsed.errorData,
      importableRows: parsed.importableRows,
      isEmpty: parsed.isEmpty,
      columnError: parsed.columnError,
    });

    const prioritizedRows = parsed.errorData.length
      ? parsed.errorData.concat(parsed.siteWithNearSites)
      : parsed.importableRows;
    setGridData(prioritizedRows.slice(0, PAGE_SIZE));
  }, [
    columnTypes,
    data,
    formValues.skipFirstRow,
    isNetworkSites,
    siteCount,
    siteNameTemplate,
    sites,
  ]);

  useEffect(() => {
    setSlicedData(gridData.slice(0, PAGE_SIZE));
  }, [gridData]);

  const headerRow = useMemo(() => {
    if (formValues.skipFirstRow && data.length > 0) {
      return Array.from({ length: columnTypes.length }, (_, index) => {
        return data[0]?.[index] || formatMessage(additionalMessages.csvEmptyHeader);
      });
    }
    return Array.from({ length: columnTypes.length }, (_, index) =>
      formatMessage(additionalMessages.csvColumnNumber, {
        number: index + 1,
      })
    );
  }, [columnTypes.length, data, formValues.skipFirstRow, formatMessage]);

  const csvColumnTypeOptions = useMemo(() => {
    const options = [
      {
        key: 'name',
        value: 'name',
        text: formatMessage(additionalMessages.name),
      },
      {
        key: 'lat',
        value: 'lat',
        text: formatMessage(additionalMessages.latitude),
      },
      {
        key: 'lon',
        value: 'lon',
        text: formatMessage(additionalMessages.longitude),
      },
      {
        key: 'utm',
        value: 'utm',
        text: formatMessage(additionalMessages.utm),
      },
      {
        key: 'mgrs',
        value: 'mgrs',
        text: formatMessage(additionalMessages.mgrs),
      },
      {
        key: 'max_height_m',
        value: 'max_height_m',
        text: `${formatMessage(additionalMessages.maxHeight)} (${formatMessage(
          additionalMessages.meters
        )})`,
      },
      {
        key: 'max_height_ft',
        value: 'max_height_ft',
        text: `${formatMessage(additionalMessages.maxHeight)} (${formatMessage(
          additionalMessages.feet
        )})`,
      },
      {
        key: 'description',
        value: 'description',
        text: formatMessage(additionalMessages.description),
      },
    ];

    if (isNetworkSites) {
      options.push({
        key: 'node_type',
        value: 'node_type',
        text: formatMessage(additionalMessages.nodeType),
      });
    }

    options.push({
      key: 'ignore',
      value: 'ignore',
      text: formatMessage(additionalMessages.ignore),
    });

    return options;
  }, [formatMessage, isNetworkSites]);

  const columnTypeLabel = (columnType: SiteCsvColumnErrorType) => {
    switch (columnType) {
      case 'name':
        return formatMessage(additionalMessages.name);
      case 'lat':
        return formatMessage(additionalMessages.latitude);
      case 'lon':
        return formatMessage(additionalMessages.longitude);
      case 'utm':
        return formatMessage(additionalMessages.utm);
      case 'mgrs':
        return formatMessage(additionalMessages.mgrs);
      case 'max_height':
        return formatMessage(additionalMessages.maxHeight);
      case 'max_height_m':
        return `${formatMessage(additionalMessages.maxHeight)} (${formatMessage(
          additionalMessages.meters
        )})`;
      case 'max_height_ft':
        return `${formatMessage(additionalMessages.maxHeight)} (${formatMessage(
          additionalMessages.feet
        )})`;
      case 'description':
        return formatMessage(additionalMessages.description);
      case 'node_type':
        return formatMessage(additionalMessages.nodeType);
      default:
        return formatMessage(additionalMessages.ignore);
    }
  };

  const columnErrorMessage = errData.columnError
    ? formatMessage(
        errData.columnError.kind === 'duplicate'
          ? additionalMessages.csvDuplicateColumn
          : additionalMessages.csvMissingColumn,
        {
          columnName: columnTypeLabel(errData.columnError.columnType),
        }
      )
    : null;

  const handleChange = (e: FormEvent<HTMLElement>, fieldData: any) => {
    setFormValues((previous) => ({
      ...previous,
      [fieldData.name]: fieldData.value ?? fieldData.checked,
    }));
  };

  const handleColumnTypeChange = (index: number, value: SiteCsvColumnType) => {
    setColumnTypes((previous) =>
      previous.map((columnType, columnIndex) =>
        columnIndex === index ? value : columnType
      )
    );
  };

  const sampleSitesCsvUrl = getSampleSitesCsvUrl(groupKind);
  const errorMessageId = getImportErrorMessageId(groupKind);
  const canCreateSites =
    !errData.error && !isLoading && errData.importableRows.length > 0;

  const handleCreateSites = useCallback(async () => {
    setIsLoading(true);
    try {
      await createSites(
        dispatch,
        projectId,
        [],
        groupKind,
        closeFunc,
        'm',
        buildSiteCsvPostData(errData.importableRows)
      );
    } finally {
      setIsLoading(false);
    }
  }, [closeFunc, dispatch, errData.importableRows, groupKind, projectId]);

  useEffect(() => {
    onSubmitStateChange({
      canSubmit: canCreateSites,
      isSubmitting: isLoading,
      onSubmit: handleCreateSites,
    });
  }, [canCreateSites, handleCreateSites, isLoading, onSubmitStateChange]);

  useEffect(
    () => () => {
      onSubmitStateChange(null);
    },
    [onSubmitStateChange]
  );

  return (
    <>
      <Form>
        <Form.Group inline style={{ justifyContent: 'space-between' }}>
          <div
            className="ui form d-flex inline fields"
            style={{ marginBottom: '0em' }}
          >
            <Form.Checkbox
              name="skipFirstRow"
              checked={formValues.skipFirstRow}
              onChange={(e, fieldData) => handleChange(e, fieldData)}
              label={formatMessage(additionalMessages.fileHeaderMsg)}
            />
          </div>
          <div>
            {columnErrorMessage && (
              <div className="custom-error">{columnErrorMessage}</div>
            )}
            {!columnErrorMessage && errData.error && !errData.isEmpty && (
              <div className="custom-error">
                {formatMessage(additionalMessages.errorFieldMsg)}
              </div>
            )}
            {errData.error && errData.isEmpty && (
              <div className="custom-error">
                {formatMessage(additionalMessages.emptyOrCorruptFile)}
              </div>
            )}
            {errData.warning && (
              <div className="custom-warning">
                {formatMessage(additionalMessages.siteWarningMsg)}
              </div>
            )}
            {errData.truncate && (
              <div className="custom-warning">
                {formatMessage(additionalMessages.warningTruncate)}
              </div>
            )}
          </div>
          {errData.error && (
            <Label basic color="red">
              {formatMessage(additionalMessages.errorSites)}:{' '}
              {errData.errorSites.length}
            </Label>
          )}
          {errData.warning && (
            <Label className="custom-warning">
              {formatMessage(additionalMessages.warningSites)}:{' '}
              {errData.siteWithNearSites.length}
            </Label>
          )}
          <Label>
            {formatMessage(additionalMessages.totalSites)}: {totalSites.current}
          </Label>
        </Form.Group>
        {errData.error && (
          <div className="custom-error" style={{ marginBottom: '0.8em' }}>
            <FormattedMessage
              id={errorMessageId}
              values={{
                a: (msg: any) => (
                  <a
                    target="_blank"
                    rel="noopener noreferrer"
                    href={sampleSitesCsvUrl}
                  >
                    {msg}
                  </a>
                ),
              }}
            />
          </div>
        )}
      </Form>
      <div className="site-import-table mb-4">
        <Table celled compact>
          <Table.Header>
            <Table.Row>
              {columnTypes.map((columnType, index) => (
                <Table.HeaderCell key={`selector-${index}`}>
                  <Form.Select
                    aria-label={formatMessage(
                      additionalMessages.csvSelectColumnType,
                      { number: index + 1 }
                    )}
                    fluid
                    options={csvColumnTypeOptions}
                    value={columnType}
                    onChange={(_, fieldData) =>
                      handleColumnTypeChange(
                        index,
                        fieldData.value as SiteCsvColumnType
                      )
                    }
                    style={{ margin: 0, minWidth: '10rem' }}
                  />
                </Table.HeaderCell>
              ))}
            </Table.Row>
            <Table.Row>
              {headerRow.map((value: string, index: number) => (
                <Table.HeaderCell key={`header-${index}`}>
                  {value || formatMessage(additionalMessages.csvEmptyHeader)}
                </Table.HeaderCell>
              ))}
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {slicedData.map((row, rowIndex: number) => (
              <Table.Row
                key={`${row.data[0] || 'row'}-${rowIndex}`}
                className={row.nearbySite ? 'custom-warning' : ''}
              >
                {row.data.map((value: string, columnIndex: number) => (
                  <Table.Cell
                    key={`${rowIndex}-${columnIndex}`}
                    negative={row.errors.includes(columnIndex)}
                    className={
                      row.truncates?.includes(columnIndex) ? 'custom-warning' : ''
                    }
                  >
                    {value}
                  </Table.Cell>
                ))}
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      </div>
    </>
  );
}

export default injectIntl(CSVSiteValidation);
